import type { ApiErrorBody } from '@skhub/contracts';

/** A failed API call, carrying what the UI needs to explain it. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
    public readonly correlationId?: string,
  ) {
    super(message);
  }
}

let csrfToken = '';
export const setCsrfToken = (t: string) => {
  csrfToken = t;
};

/** Fired when the server says the session is gone, so the app can return to sign-in. */
export const SESSION_EXPIRED_EVENT = 'skhub:session-expired';

type Query = Record<string, string | number | boolean | null | undefined>;

export function qs(params: Query = {}) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function call<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      ...init,
    });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the Smart Assets server. Check your connection and try again.');
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON (e.g. a proxy error page) */
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    if (res.status === 401 && !path.startsWith('/auth/')) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    throw new ApiError(
      res.status,
      err?.code ?? 'http_error',
      err?.message ?? (res.status >= 500 ? 'The server could not complete this request.' : `Request failed (${res.status}).`),
      err?.fields,
      err?.correlationId,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, params?: Query, init?: RequestInit) => call<T>('GET', path + qs(params), undefined, init),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body ?? {}),
  del: <T>(path: string) => call<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => call<T>('POST', path, form),
};

/** Downloads an authenticated file (xlsx) without navigating away. */
export async function download(path: string, params?: Query) {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}${qs(params)}`, { credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the Smart Assets server. Check your connection and try again.');
  }
  if (!res.ok) {
    let msg = 'The export could not be created.';
    try {
      msg = ((await res.json()) as ApiErrorBody).error.message;
    } catch {
      /* keep default */
    }
    throw new ApiError(res.status, 'export_failed', msg);
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'export.xlsx';
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
